<template>
	<div
		v-if="$options.chatbotEnable && !hideChatbotButton"
		ref="chatBotWrapper"
		class="chat-bot"
		:class="[chatClasses, chatInFooterClass]"
	>
		<div
			class="chat-bubble-content"
			:class="{
				'chat-bubble-content--disabled': userBanned || queryLimitExceeded
			}"
			@click="openChat"
		>
			<svgicon name="chatbot/live-help" width="20.57" height="24" />
			<p class="chat-bot-text">{{ $t('chatbotLabel') }}</p>
		</div>
		<div class="chat" :class="chatClasses">
			<div class="header">
				<span class="title">
					<img
						src="@/assets/images/chatbot/chatbot-avatar.png"
						alt="chatbot-avatar"
						class="chatbot-avatar"
					/>
					{{ $t('chatbotTitle') }}
				</span>
				<div class="header--spacer">
					<div class="tooltip-container">
						<div
							v-if="!!strikesTotal"
							class="counter-strike"
							@click="showTooltip = !showTooltip"
						>
							<svgicon
								name="chatbot/report"
								class="svg-fill report-icon"
								width="18"
								height="18"
							/>
							{{ strikesTotal }}/{{ numberStrikesToBan }}
							<div v-if="showTooltip" class="tooltip">
								{{ $t('strikeLabel') }}
							</div>
						</div>
					</div>
					<button @click="closeChat">
						<svgicon
							name="chatbot/keyboard-arrow-down"
							width="24"
							height="24"
							class="icon-arrow-down"
						/>
					</button>
				</div>
			</div>
			<ul ref="chatBotMessages" class="messages">
				<li class="other">
					<pre class="text-message">{{ $t('introMessageLabel') }}</pre>
				</li>
				<div v-if="chatMessages.length === 0 && !loading">
					<li
						v-for="defaultQuestion in defaultQuestions"
						:key="defaultQuestion.id"
						class="default-question"
						@click="() => handleDefaultQuestions(defaultQuestion)"
					>
						{{ $t(defaultQuestion.question) }}
					</li>
				</div>
				<li
					v-for="(message, index) in chatMessages"
					:key="message.id"
					:class="{
						other: !message.isMyMessage,
						self: message.isMyMessage,
						'strike-message': message.isStrike,
						'warning-message': !!message.isWarning
					}"
				>
					<pre
						v-if="message.message"
						class="text-message"
						v-html="message.message"
					/>
					<div v-if="message.isMyMessage" class="text-message-counter">
						{{ getMyMessageIndex(index) }}/{{ numberQueryLimit }}
					</div>
				</li>
				<li v-if="loading && chatMessages.length" class="other other--loading">
					<div class="loading-dot" />
					<div id="second-loading-dot" class="loading-dot" />
					<div id="third-loading-dot" class="loading-dot" />
				</li>
			</ul>
			<div class="footer">
				<div v-if="showArrowIcon" class="icon-container" @click="hideArrow">
					<svgicon name="chatbot/arrow_circle_down" width="31" height="32" />
				</div>
				<div>
					<textarea
						ref="chatTextArea"
						v-model="chatTextBox"
						:disabled="textBoxDisabled || userBanned || queryLimitExceeded"
						class="footer-text-box"
						:placeholder="
							userBanned
								? $t('chatbotPlaceholderDisabled')
								: queryLimitExceeded
								? $t('chatbotPlaceholderExceeded')
								: $t('chatbotPlaceholder')
						"
						cols="30"
						rows="2"
						maxlength="1000"
						@keydown="onMetaAndEnter"
					></textarea>
					<div
						class="footer-message-counter"
						:class="{ active: !!chatTextBox }"
					>
						{{ currentQueryLimit }}/{{ numberQueryLimit }}
					</div>
				</div>
				<button
					id="sendMessage"
					:disabled="textBoxDisabled || !chatTextBox"
					class="send-button"
					:class="{
						'send-button--disabled': sendMessagesDisabled || !chatTextBox
					}"
					@click="() => sendNewMessage()"
				>
					{{ $t('chatBotSend') }}
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import '../icons/chatbot';
import ChatbotService from '@/services/ChatbotService';
import MessagesService from '@/services/MessagesService';
import {
	BITMASK,
	keyboardKeyEvents,
	DEFAULT_CHATBOT_QUESTION
} from '../../helpers/constants';
export default {
	name: 'ChatBot',
	props: {
		isChatInFooter: {
			type: Boolean,
			default: false
		},
		hideChatbotButton: {
			type: Boolean,
			default: false
		}
	},
	chatbotEnable: JSON.parse(process.env.VUE_APP_JA_CHATBOT_ENABLE),
	data() {
		return {
			chatTextBox: null,
			entered: false,
			expanded: false,
			textBoxDisabled: true,
			textBoxMessage: '',
			chatMessages: [],
			historyNumberMessage: 10,
			user: {
				id: 1
			},
			chatbotStrikeMessages: {
				1: this.$t('firstStrike'),
				2: this.$t('secondStrike'),
				3: this.$t('thirdStrike'),
				instantBan: this.$t('instantBan'),
				unknow: this.$t('unknow'),
				notify: this.$t('notify')
			},
			loading: true,
			strikesTotal: 0,
			showArrowIcon: false,
			numberStrikesToBan: 3,
			numberQueryLimit: 25,
			numberNotifyQueryLimit: 20,
			instantBan: false,
			queryLimitExceeded: false,
			showTooltip: false,
			defaultQuestions: DEFAULT_CHATBOT_QUESTION
		};
	},
	computed: {
		chatClasses() {
			return {
				enter: !!this.entered,
				expand: !!this.expanded
			};
		},
		chatInFooterClass() {
			return {
				'chat-bot--in-footer': !!this.isChatInFooter
			};
		},
		chatMessagesList() {
			return this.$refs.chatBotMessages;
		},
		chat() {
			return this.$refs.chatBotWrapper;
		},
		chatTextBoxElement() {
			return this.$refs.chatTextArea;
		},
		userData() {
			return this.$store.getters.userData;
		},
		sendMessagesDisabled() {
			return this.loading || this.userBanned || this.queryLimitExceeded;
		},
		currentQueryLimit() {
			return this.chatMessages.filter(message => message.isMyMessage).length;
		},
		userBanned() {
			return this.strikesTotal >= this.numberStrikesToBan || this.instantBan;
		}
	},
	watch: {
		queryLimitExceeded(newValue) {
			if (newValue) {
				this.chatTextBox = '';
			}
		},
		userBanned(newValue) {
			if (newValue) {
				this.chatTextBox = '';
			}
		}
	},
	async mounted() {
		try {
			if (!this.$options.chatbotEnable) return;

			const response = await ChatbotService.getHistory(this.userData.studentId);
			const chatHistory = response.data || [];

			if (chatHistory.length > 0) {
				const lastMessage = chatHistory[chatHistory.length - 1];

				this.instantBan = this.handleInstantBan(lastMessage.type);
				const currentStrikes = this.handleGetNumberStrikes(lastMessage.type);
				this.strikesTotal =
					currentStrikes > this.numberStrikesToBan
						? this.numberStrikesToBan
						: currentStrikes;
			}

			this.chatMessages = chatHistory.map(({ text, type }, index) => {
				let message = text;

				const isUnknow = this.handleUnknow(type);
				const isInstantBan = this.handleInstantBan(type);
				const isActiveStrike = this.handleActiveStrike(type);
				const isMyMessage = this.handleIsMyMessage(type);
				let numberStrike = this.handleGetNumberStrikes(type);
				if (numberStrike > this.numberStrikesToBan)
					numberStrike = this.numberStrikesToBan;

				if (!isMyMessage) {
					if (isInstantBan) {
						message = this.chatbotStrikeMessages.instantBan;
					} else if (isUnknow) {
						message = this.chatbotStrikeMessages.unknow;
					} else if (isActiveStrike) {
						message = this.chatbotStrikeMessages[numberStrike];
					}
				}
				return {
					id: index + 1,
					message,
					isMyMessage,
					isStrike: isActiveStrike && !isMyMessage,
					type,
					isWarning: false
				};
			});

			await this.validQueryLimit();
			this.chatMessagesList.scrollTop = this.chatMessagesList.scrollHeight;
		} catch (error) {
			console.error(error);
		} finally {
			this.loading = false;
		}
	},
	methods: {
		getChabotEnabledValue() {
			// TODO: Call services to get is Enabled chat
			return JSON.parse(process.env.VUE_APP_JA_CHATBOT_ENABLE);
		},
		validQueryLimit() {
			if (this.currentQueryLimit === this.numberNotifyQueryLimit) {
				this.chatMessages.push({
					id: this.chatMessages.length + 1,
					message: this.$t('queryWarningMessage', {
						studentName: this.userData.firstName
					}),
					isWarning: true,
					isMyMessage: false,
					isStrike: false
				});
			}

			this.queryLimitExceeded = this.currentQueryLimit >= this.numberQueryLimit;

			if (this.queryLimitExceeded) {
				this.chatMessages.push({
					id: this.chatMessages.length + 1,
					message: this.$t('requestsExceededMessage'),
					isWarning: true,
					isMyMessage: false,
					isStrike: false
				});
			}
		},
		handleInstantBan(type = 0) {
			return (type & BITMASK.threat) === BITMASK.threat;
		},
		handleUnknow(type = 0) {
			return (type & BITMASK.unknown) === BITMASK.unknown;
		},
		handleError(type = 0) {
			return (type & BITMASK.error) === BITMASK.error;
		},
		handleActiveStrike(type = 0) {
			return (type & BITMASK.activeStrike) === BITMASK.activeStrike;
		},
		handleNotify(type = 0) {
			return (type & BITMASK.notify) === BITMASK.notify;
		},
		handleGetNumberStrikes(type = 0) {
			return (type & BITMASK.numberStrikes) >> 4;
		},
		handleIsMyMessage(type = 0) {
			return (type & BITMASK.question) === BITMASK.question;
		},
		scrollDown() {
			this.chatMessagesList.scrollTo({
				top: this.chatMessagesList.scrollHeight,
				behavior: 'smooth'
			});
		},
		hideArrow() {
			this.scrollDown();
			this.showArrowIcon = false;
		},
		openChat() {
			if (!this.expanded) {
				this.expanded = true;
				this.entered = true;
				this.textBoxDisabled = false;

				this.$nextTick(() => {
					this.chatTextBoxElement.focus();
					if (this.chatMessages.length) {
						this.chatMessagesList.scrollTop = this.chatMessagesList.scrollHeight;
					}
				});
			}
		},
		closeChat() {
			this.expanded = false;
			this.entered = false;
			this.textBoxDisabled = true;
		},
		async sendNewMessage(textMessage) {
			const textBoxMessage = textMessage ?? this.chatTextBox;
			if (!textBoxMessage || this.sendMessagesDisabled) return;

			this.loading = true;
			this.showArrowIcon = false;

			const history = this.chatMessages.length
				? this.chatMessages
						.filter(
							item => !item.isWarning && !this.handleActiveStrike(item.type)
						)
						.slice(-this.historyNumberMessage)
						.map(({ message, type }) => ({ message, type }))
				: [];

			this.chatMessages.push({
				id: this.chatMessages.length + 1,
				message: textBoxMessage,
				isMyMessage: true,
				isStrike: false,
				isWarning: false
			});
			const data = {
				student_id: this.userData.studentId,
				question: textBoxMessage,
				history: history,
				strikes: this.strikesTotal
			};

			// clean out old message
			this.chatTextBox = '';
			// focus on input
			this.chatTextBoxElement.focus();

			await this.$nextTick(() => {
				this.scrollDown();
			});

			await ChatbotService.sendAnswer(data)
				.then(({ data: dataResponse }) => {
					let message;
					const type = dataResponse.type;
					if (dataResponse.safe) {
						message = dataResponse.message;
					} else {
						const isUnknow = this.handleUnknow(type);
						const isInstantBan = this.handleInstantBan(type);
						const notifyThis = this.handleNotify(dataResponse.type);
						let numberStrike = this.handleGetNumberStrikes(type);
						if (numberStrike > this.numberStrikesToBan)
							numberStrike = this.numberStrikesToBan;

						if (isInstantBan) {
							message = this.chatbotStrikeMessages.instantBan;
						} else if (isUnknow) {
							message = this.chatbotStrikeMessages.unknow;
						} else {
							message = this.chatbotStrikeMessages[numberStrike];
						}

						this.strikesTotal =
							numberStrike > this.numberStrikesToBan
								? this.numberStrikesToBan
								: numberStrike;

						if (notifyThis) {
							const notifyMessage = this.$t('notifyMessage', {
								studentName: this.userData.firstName,
								studentLastName: this.userData.lastName,
								queryContent: data.question
							});
							MessagesService.send(this.userData.userId, notifyMessage); //.then(() => {});
							//message = message; // + " " + this.chatbotStrikeMessages.notify;
						}
					}
					this.chatMessages[this.chatMessages.length - 1].type =
						(type & ~BITMASK.answer) | BITMASK.question;

					this.chatMessages.push({
						id: this.chatMessages.length + 1,
						message: message,
						isMyMessage: false,
						isWarning: false,
						type: type,
						isStrike: this.handleActiveStrike(type)
					});

					this.validQueryLimit();

					this.$nextTick(() => {
						if (message.length > 350) {
							this.chatMessagesList.scrollBy({
								top: 200,
								behavior: 'smooth'
							});
							this.showArrowIcon = true;
						} else {
							this.scrollDown();
						}
					});
				})
				.catch(error => {
					console.error(error);

					const messages = this.chatMessages;

					const secondLastMessage = messages[messages.length - 2];
					const lastMessage = messages[messages.length - 1];

					let secondLastType = secondLastMessage ? secondLastMessage.type : 0;

					if (lastMessage) {
						this.chatMessages[this.chatMessages.length - 1].type =
							(secondLastType & ~BITMASK.answer & ~BITMASK.answer) |
							BITMASK.question;
						this.chatMessages[this.chatMessages.length - 1].isWarning = true;
					}

					const newType = (secondLastType & ~BITMASK.question) | BITMASK.answer;

					messages.push({
						id: messages.length + 1,
						message: this.$t('chatbotErrorMessage'),
						isMyMessage: false,
						isWarning: false,
						type: newType,
						isStrike: false
					});
				})
				.finally(() => {
					this.loading = false;
				});
		},
		onMetaAndEnter(event) {
			if (
				(event.metaKey || event.ctrlKey) &&
				event.keyCode === keyboardKeyEvents.ENTER_KEY_CODE
			) {
				const textarea = event.target;
				const start = textarea.selectionStart;
				const end = textarea.selectionEnd;
				if (this.chatTextBox) {
					this.chatTextBox =
						this.chatTextBox.slice(0, start) +
						'\n' +
						this.chatTextBox.slice(end);
				}
				this.chatTextBoxElement.scrollTop = this.chatTextBoxElement.scrollHeight;
			} else if (event.keyCode === keyboardKeyEvents.ENTER_KEY_CODE) {
				event.preventDefault();
				this.sendNewMessage();
			}
		},
		getMyMessageIndex(currentIndex) {
			let count = 0;
			for (let i = 0; i <= currentIndex; i++) {
				if (this.chatMessages[i].isMyMessage) {
					count++;
				}
			}
			return count;
		},
		async handleDefaultQuestions(defaultQuestion) {
			this.loading = true;
			this.showArrowIcon = false;
			this.chatMessages.push({
				id: this.chatMessages.length + 1,
				message: this.$t(defaultQuestion.question),
				isMyMessage: true,
				isWarning: false,
				type: 1,
				isStrike: false
			});

			this.chatTextBoxElement.focus();

			await this.$nextTick(() => {
				this.scrollDown();
			});

			setTimeout(() => {
				this.chatMessages.push({
					id: this.chatMessages.length + 1,
					message: this.$t(defaultQuestion.answer),
					isMyMessage: false,
					isWarning: false,
					type: 2,
					isStrike: false
				});
				this.$nextTick(() => {
					if (defaultQuestion.answer.length > 350) {
						this.chatMessagesList.scrollBy({
							top: 200,
							behavior: 'smooth'
						});
						this.showArrowIcon = true;
					} else {
						this.scrollDown();
					}
				});
				this.loading = false;
			}, 1000);
		}
	}
};
</script>

<style lang="scss" scoped>
// Variables
$color-white: #ffffff;
$color-blue: #6694e3;
$color-chat-blue: #004cd0;
$color-black: #262626;
$color-message-bubble: #f3f8fe;
$scrollbar-width: 4px;
$chat-textarea-color: #64737d;
$chat-messages-color: $color-white;
$chat-messages-bg-color: $color-blue;
$chat-messages-border-color: $color-blue;
$chat-thread-bgd-color: $color-blue;
$chat-thread-msg-arrow-size: 10px;
$chat-thread-avatar-size: 25px;
$chat-thread-offset: 28px;
$chat-scrollbar-color: #c1c1c1;
$chat-scrollbar-track-color: #f1f1f1;
$chat-disabled-button: #edf1f2;
$chat-disabled-text: #c2c6c9;
$send-disabled-button: #d9d9d9;
$send-disabled-text: #64737d;
$default-shadow-color: $color-chat-blue;
$chat-error-color: #c51400;
$chat-bg-warning-color: #e9e9e9;

// Mixins
@mixin fancy-background {
	background: $color-white;
	background-repeat: no-repeat;
	background-attachment: fixed;
}

@mixin fancy-scrollbar {
	&::-webkit-scrollbar {
		width: $scrollbar-width;
	}
	&::-webkit-scrollbar-track {
		background-color: $chat-scrollbar-track-color;
	}
	&::-webkit-scrollbar-thumb {
		background-color: $chat-scrollbar-color;
	}
}

.chat-bot {
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	color: $color-chat-blue;
	position: fixed;
	bottom: 32px;
	right: 23.43px;
	width: 141.57px;
	height: 44px;
	border-radius: 33px;
	@include fancy-background;
	z-index: 999999;

	.chat-bubble-content {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 33px;
		z-index: 1;
		outline: $chat-messages-border-color solid 1px;
		box-shadow: 0px 4px 4px 0px rgba(0, 76, 208, 0.24);

		&:hover {
			background: $color-chat-blue;
			color: $color-white;
			outline: none;
		}

		&--disabled {
			background: $chat-disabled-button;
			color: $chat-disabled-text;
			outline: none;
			box-shadow: unset;

			&:hover {
				background: $chat-disabled-button;
				color: $chat-disabled-text;
				outline: none;
			}
		}
	}

	&-text {
		line-height: 18.75px;
		font-family: 'RobotoMedium';
		padding-left: 5px;
	}

	&.enter {
		transform: translateY(0);
		opacity: 0.6;
		outline: $chat-messages-border-color solid 1px;
	}

	&.expand {
		width: 340px;
		max-height: 443px;
		height: 443px;
		border-radius: 5px;
		cursor: auto;
		opacity: 1;

		.chat-bubble-content {
			display: none;
		}
	}

	&:focus {
		outline: 0;
		box-shadow: 0 0 3pt 2pt rgba(#0ec879, 0.3);
	}

	button {
		background: transparent;
		border: 0;
		color: $color-white;
		text-transform: uppercase;
		border-radius: 3px;
		cursor: pointer;
	}
}

.chat {
	flex-direction: column;
	position: absolute;
	opacity: 0;
	width: 1px;
	height: 1px;
	border-radius: 50%;
	margin: auto;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: none;

	&.enter {
		opacity: 1;
		border-radius: 8px;
		width: auto;
		height: auto;
		display: flex;
	}

	.header {
		flex-shrink: 0;
		padding: 10px;
		display: flex;
		background: transparent;
		position: relative;
		height: 50px;

		&--spacer {
			display: flex;
			align-items: center;
			justify-content: space-between;

			.counter-strike {
				background-color: $chat-error-color;
				color: $color-white;
				height: 24px;
				font-family: 'RobotoRegular';
				padding: 3px 5px;
				border-radius: 25px;
				width: 64px;
				display: flex;
				justify-content: center;
				gap: 4px;
				cursor: pointer;
			}
		}

		.title {
			flex-grow: 1;
			flex-shrink: 1;
			padding: 0 5px;
			color: $color-black;
			display: flex;
			align-items: center;
			font-family: 'RobotoRegular';
			font-weight: 400;
		}

		.chatbot-avatar {
			padding-right: 10px;
		}

		button {
			flex-shrink: 0;
			color: $color-black;

			.icon-arrow-down {
				color: $color-chat-blue;
			}
		}

		&:after {
			content: '';
			width: 100%;
			height: 14px;
			position: absolute;
			bottom: -10px;
			left: 0;
			z-index: 1;
			background: linear-gradient(
				360deg,
				rgba(217, 217, 217, 0) 8%,
				#dff2fd 100%
			);
		}
	}

	.messages {
		padding: 10px;
		margin: 0;
		list-style: none;
		overflow-y: scroll;
		overflow-x: hidden;
		flex-grow: 1;
		border-radius: 4px;
		background: transparent;
		padding-bottom: 0;

		li {
			position: relative;
			clear: both;
			display: inline-block;
			padding: 16px;
			margin: 0 0 10px 0;
			border-radius: 8px;
			color: $chat-textarea-color;
			background-color: $chat-messages-bg-color;
			word-wrap: break-word;
			max-width: 280px;
			font-weight: 400;
			font-family: 'RobotoRegular';

			.text-message {
				word-wrap: break-word;
				white-space: pre-wrap;
				font-family: 'RobotoRegular';
				margin: 0;
			}
			.text-message-counter {
				color: $color-chat-blue;
				font-size: 12px;
				font-family: 'RobotoRegular';
				font-weight: 400;
				text-align: right;
				position: absolute;
				left: -35px;
				bottom: 5px;
				// top: 50%;
				// transform: translateY(-50%);
			}
		}
		li.other {
			float: left;
			margin-right: $chat-thread-offset;
			background: $color-message-bubble;
		}

		li.other:after {
			border-right: $chat-thread-msg-arrow-size solid transparent;
			right: -$chat-thread-msg-arrow-size;
		}

		li.default-question {
			cursor: pointer;
			background-color: $color-chat-blue;
			color: $color-white;
			border-radius: 20px;
			padding: 15px 10px;
			margin-right: 10px;
			margin-bottom: 10px;
			font-family: 'RobotoRegular';
			font-weight: 500;

			&:hover {
				background-color: darken($color-chat-blue, 10%);
			}
		}

		li.other.warning-message {
			font-weight: 700;
		}

		li.other.strike-message {
			color: $chat-error-color;
			background-color: $chat-bg-warning-color;
			border-color: $chat-error-color;
			&:after {
				border-right: $chat-thread-msg-arrow-size solid transparent;
				right: -$chat-thread-msg-arrow-size;
			}
		}
		li.self {
			float: right;
			margin-left: 40px;
			border: solid 1px $color-blue;
			background-color: $color-white;
		}

		li.self:after {
			border-left: $chat-thread-msg-arrow-size solid transparent;
			left: -$chat-thread-msg-arrow-size;
		}

		li.other--loading {
			display: flex;
			.loading-dot {
				height: 10px;
				width: 10px;
				margin-right: 3px;
				background-color: $active-page-color;
				border-radius: 50%;
				animation: blinking 1s infinite;

				@keyframes blinking {
					0%,
					100% {
						transform: scale(0.3);
					}
					50% {
						transform: scale(1);
						background-color: $url-color;
					}
				}
			}
			#second-loading-dot {
				animation-delay: 0.2s;
			}
			#third-loading-dot {
				animation-delay: 0.4s;
			}
		}
	}

	.footer {
		flex-shrink: 0;
		display: flex;
		padding: 10px;
		max-height: 78px;
		height: 78px;
		background: transparent;
		position: relative;

		.icon-container {
			position: absolute;
			top: -42px;
			left: 0;
			height: 42px;
			width: 100%;
			display: flex;
			cursor: pointer;
			justify-content: center;
			background: linear-gradient(180deg, rgba(217, 217, 217, 0) 0%, #fff 100%);
		}

		.footer-text-box {
			border-radius: 3px;
			border: none;
			width: 209px;
			margin-right: 10px;
			color: $chat-textarea-color;
			overflow-y: auto;
			resize: none;
			@include fancy-scrollbar;
			font-family: 'RobotoMedium';
			font-weight: 500;
			outline: none;
			line-height: normal;
			font-size: 16px;
		}

		.footer-message-counter {
			color: $send-disabled-button;
			font-weight: 500;
			font-size: 16px;
			font-family: 'RobotoMedium';
			text-align: right;
			&.active {
				color: $color-chat-blue;
			}
		}

		.send-button {
			outline: $chat-messages-border-color solid 1px;
			color: $chat-messages-bg-color;
			padding: 20px 32px 20px 32px;
			width: 101px;
			border-radius: 30px;
			text-transform: capitalize;
			font-family: 'RobotoMedium';
			line-height: 16px;
			font-weight: 500;
			font-size: 16px;

			&:hover {
				background: $color-chat-blue;
				color: $color-white;
				outline: none;
			}

			&--disabled {
				background: $send-disabled-button;
				outline: none;
				color: $send-disabled-text;
				pointer-events: none;
			}
		}
	}
}
.tooltip-container {
	position: relative;
	display: inline-block;
}
.tooltip {
	position: absolute;
	top: 20%;
	right: 55%;
	width: 244px;
	min-height: 84px;
	padding: 10px;
	background-color: $chat-bg-warning-color;
	color: $chat-error-color;
	font-size: 16px;
	z-index: 1000;
	border: 1px solid $chat-error-color;
}

.chat-bot--in-footer {
	position: absolute;
	top: -76px; // 44px + 32px

	&.enter {
		top: -475px; //443px + 32px
	}
}
</style>
